import React, {useEffect} from 'react';
import DateFnsUtils from '@date-io/date-fns';
import { addDays, format } from 'date-fns';
import { useParams, Link, useHistory} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import {
  getCustomerSubscriptionApi,
  editCustomerSubscriptionApi,
  editCustomerSubscriptionPriceApi,
  getSARPUpdateConfigApi
} from 'api';
import { convertUTCDateToLocalDate } from 'helpers/date';
import { TextFormattedNumber } from 'components';
import {
  MuiPickersUtilsProvider,
  DatePicker,
} from '@material-ui/pickers';
import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { Formik } from 'formik';
import * as yup from 'yup';
import { subscriptionStatus } from 'helpers/customerSubscriptions';


const DetailsForm = ({goBackPath}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {id} = useParams<{ id: string }>();
  const { editableCustomerSubscription } = useSelector((state: IReduxState) => state.customer_subscriptions);
  const { isSARPUpdateEnabled } = useSelector((state: IReduxState) => state.store_configs);

  const {
    description,
    status,
    created_at,
    extension_attributes,
    last_run,
    next_run,
    frequency_count,
    frequency_unit,
    run_count,
    subtotal,
    customer_id,
    length
  } = editableCustomerSubscription || {};

  useEffect(() => {
    getSARPUpdateConfigApi({dispatch});
    getCustomerSubscriptionApi({dispatch, id});
  }, []);

  const handleSaveSubscription = (values) => {
    const subscription = {
      ...editableCustomerSubscription,
      description: values.description,
      frequency_unit: values.frequency_unit,
      frequency_count: values.frequency_count,
      next_run: values.next_run,
      length: values.length,
      additional_information: {
        ...editableCustomerSubscription.additional_information,
        notes: values.notes
      }
    };
    editCustomerSubscriptionApi({dispatch, subscription});

    if (values.subtotal !== subtotal) {
      editCustomerSubscriptionPriceApi({
        dispatch,
        subscriptionId: id,
        price: parseFloat(values.subtotal)
      });
    }
  };

  const lustRunDate = convertUTCDateToLocalDate(last_run || new Date());
  const startDate = convertUTCDateToLocalDate(created_at || new Date());
  const formatedLustRunDate = format(lustRunDate, 'MM/dd/yyyy hh:mm:ss aa');
  const formatedStartDate = format(startDate, 'MM/dd/yyyy hh:mm:ss aa');

  return (
    <>
      <Formik
        initialValues={{
          description: description || '',
          frequency_unit: frequency_unit || '',
          frequency_count: frequency_count || '',
          next_run: next_run || addDays(new Date(), 1),
          length: length || 0,
          notes: extension_attributes?.notes || '',
          run_count: run_count || 0,
          subtotal: subtotal || 0
        }}
        validationSchema={yup.object({
          description: yup
            .string()
            .required('Field is required'),
          frequency_count: yup
            .number()
            .min(1, 'Frequency count must be greater than 0')
            .required('Field is required'),
          next_run: yup
            .date(),
          frequency_unit: yup
            .string()
            .required('Field is required'),
          length: yup
            .number()
            .min(0, 'Length must be greater than or equal to 0')
            .required('Field is required')
            .test(
              'valid-length',
              'Subscription length cannot be set to less than or equal to the number of times billed.',
              (value, context) => {
                const run_count = context.parent.run_count || 0;

                if (value === 0) {
                  return true;
                }

                return value > run_count;
              }
            ),
        })}
        enableReinitialize
        validateOnMount
        onSubmit={(values) => handleSaveSubscription(values)}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        }) => (
          <form noValidate autoComplete="off" className="py-4 px-2" onSubmit={handleSubmit}>
            <Grid container spacing={2} className="flex items-center">
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Product(s)
                </Typography>
                {extension_attributes?.products?.map((product) => (
                  <Typography key={product} className="ml-2">
                    {product}
                  </Typography>
                ))}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Started
                </Typography>
                <Typography className="ml-2">
                  {formatedStartDate}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Description
                </Typography>
                <TextField
                  required
                  fullWidth
                  variant="outlined"
                  label="Description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.description && Boolean(errors?.description)}
                  helperText={touched?.description && errors?.description}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Last Run
                </Typography>
                <Typography className="ml-2">
                  {formatedLustRunDate}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={`mb-2 ${!isSARPUpdateEnabled ? 'mt-5' : ''}`} variant="h6">
                  Subtotal
                </Typography>
                <Typography>
                  {isSARPUpdateEnabled ?
                    <TextFormattedNumber
                      value={subtotal}
                      prefix="$"
                    />
                    :
                    <NumberFormat
                      required
                      customInput={TextField}
                      variant="outlined"
                      label="Subtotal"
                      name="subtotal"
                      prefix="$"
                      fixedDecimalScale
                      decimalScale={2}
                      thousandSeparator
                      fullWidth
                      value={values.subtotal}
                      onValueChange={(values) => {
                        setFieldValue('subtotal', values.floatValue);
                      }}
                      onBlur={handleBlur}
                      error={touched?.subtotal && Boolean(errors?.subtotal)}
                      helperText={touched?.subtotal && errors?.subtotal}
                    />
                  }
                </Typography>
                <Typography className="ml-2 text-13 text-light-gray">
                  Note: Subtotals do not include shipping, tax, or other possible surcharges. Actual orders totals may vary over time.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Next Run
                </Typography>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    autoOk
                    fullWidth
                    variant="inline"
                    format="MM/dd/yyyy"
                    emptyLabel="mm/dd/yyyy"
                    name="next_run"
                    label="Next Run"
                    value={values.next_run}
                    inputVariant="outlined"
                    onChange={(date) => {
                      setFieldValue('next_run', date);
                    }}
                    onBlur={handleBlur}
                    error={Boolean(errors?.next_run)}
                    helperText={errors?.next_run}
                    InputProps={{
                      endAdornment: (
                        <CalendarTodayIcon className="text-muted text-16" />
                      )
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Status
                </Typography>
                <Typography className="ml-2">
                  {subscriptionStatus[status]}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Number of times billed
                </Typography>
                <Typography className="ml-2">
                  {run_count}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Customer
                </Typography>
                <Typography className="ml-2">
                  <Link
                    className="underline text-primary mr-2"
                    to={`/customers/${customer_id}/details`}
                  >
                    {`${extension_attributes?.first_name} ${extension_attributes?.last_name}`}
                  </Link>
                  {`(${extension_attributes?.email})`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Frequency: Every
                </Typography>
                <div className="flex">
                  <TextField
                    required
                    id="outlined-number"
                    fullWidth
                    type="number"
                    variant="outlined"
                    label="Frequency Count"
                    name="frequency_count"
                    inputProps={{ min: 0 }}
                    value={values.frequency_count}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.frequency_count && Boolean(errors?.frequency_count)}
                    helperText={touched?.frequency_count && errors?.frequency_count}
                  />
                  <Select
                    fullWidth
                    variant="outlined"
                    name="frequency_unit"
                    className="ml-3"
                    value={values.frequency_unit}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="year">Years</MenuItem>
                    <MenuItem value="month">Months</MenuItem>
                    <MenuItem value="week">Weeks</MenuItem>
                    <MenuItem value="day">Days</MenuItem>
                  </Select>
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Purchase Point
                </Typography>
                <Typography className="ml-2">
                  {extension_attributes?.purchase_point}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Length
                </Typography>
                <TextField
                  required
                  id="outlined-number"
                  fullWidth
                  type="number"
                  variant="outlined"
                  label="Length"
                  name="length"
                  value={values.length}
                  inputProps={{ min: 1 }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.length && Boolean(errors?.length)}
                  helperText={touched?.length && errors?.length}
                />
                <Typography className="text-13 text-light-gray">
                  Number of cycles the subscription should run (total number). 0 for indefinite
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Typography className="mb-2" variant="h6">
                  Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  variant="outlined"
                  name="notes"
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.notes && Boolean(errors?.notes)}
                  helperText={touched?.notes && errors?.notes}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="mt-4"
            >
              Save Subscription
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className="mt-4 ml-2"
              onClick={() => history.push(goBackPath)}
            >
              Go Back
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
};

export default DetailsForm;
